import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EngineeringOutlinedIcon from "@mui/icons-material/EngineeringOutlined";
import { LoadingButton } from "@mui/lab";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isSubAdminEmail } from "../../../functions/helperFunctions";
import { setSelectedRow } from "../../../store/actions/uiActions/myPermitsActions";

const MESSAGE_DURATION = 3000;

const SectionHeader = ({ icon, title }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
    {icon}
    <Typography variant="subtitle1" fontWeight={700}>
      {title}
    </Typography>
  </Stack>
);

const AdminPackageTab = () => {
  const dispatch = useDispatch();
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state?.[currentSession]?.user);
  const { selectedRow } = useSelector((state) => state.myPermitsComponent);
  const permitPackages = useSelector((state) => state.nocData?.permitPackages);
  const customerPackages = useSelector(
    (state) => state.nocData?.customerPackages,
  );
  const { subAdmins } = useSelector((state) => state.nocData || {});
  const { subAdminDomains = [] } = subAdmins || {};

  const [sendDocOwnerLoading, setSendDocOwnerLoading] = useState(false);
  const [sendDocContractorLoading, setSendDocContractorLoading] =
    useState(false);
  const [documentDownloadLoading, setDocumentDownloadLoading] = useState(false);
  const [documentMessage, setDocumentMessage] = useState({
    type: "",
    text: "",
  });
  const [accordionExpanded, setAccordionExpanded] = useState(false);
  const [selectedSignOrder, setSelectedSignOrder] = useState("contractor");
  const [originalSignOrder, setOriginalSignOrder] = useState("contractor");
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [modifyLoading, setModifyLoading] = useState(false);
  const [signOrderMessage, setSignOrderMessage] = useState({
    type: "",
    text: "",
  });
  const [copiedUrlKey, setCopiedUrlKey] = useState(null);

  const isAdmin = currentUser?.user?.role === "admin";
  const isSubAdmin = isSubAdminEmail(currentUser?.user?.email, subAdminDomains);

  const customer = useMemo(
    () => ({
      email: selectedRow?.email || currentUser?.user?.email,
      role: selectedRow?.role || currentUser?.user?.role,
    }),
    [
      currentUser?.user?.email,
      currentUser?.user?.role,
      selectedRow?.email,
      selectedRow?.role,
    ],
  );

  const sessionLinks = useMemo(() => {
    const notarization = selectedRow?.processing_permit?.notarization;

    return [
      {
        key: "owner",
        label: "Property Owner",
        icon: <PersonOutlineIcon fontSize="small" />,
        session: notarization?.owner?.notarization?.bn_session,
      },
      {
        key: "contractor",
        label: "Contractor",
        icon: <EngineeringOutlinedIcon fontSize="small" />,
        session: notarization?.contractor?.notarization?.bn_session,
      },
    ].filter(
      ({ session }) =>
        Boolean(session?.signer_url) || Boolean(session?.notary_url),
    );
  }, [selectedRow]);

  useEffect(() => {
    const signOrder =
      selectedRow?.processing_permit?.notarization?.signing_order ||
      "contractor";

    setOriginalSignOrder(signOrder);
    setSelectedSignOrder(signOrder);
    setAccordionExpanded(false);
    setConfirmModalOpen(false);
    setDocumentMessage({ type: "", text: "" });
    setSignOrderMessage({ type: "", text: "" });
    setCopiedUrlKey(null);
  }, [
    selectedRow?.id,
    selectedRow?.processing_permit?.notarization?.signing_order,
  ]);

  const showDocumentMessage = (type, text) => {
    setDocumentMessage({ type, text });
    setTimeout(
      () => setDocumentMessage({ type: "", text: "" }),
      MESSAGE_DURATION,
    );
  };

  const handleSendDocument = async (type) => {
    try {
      if (type === "owner") {
        setSendDocOwnerLoading(true);
      } else if (type === "contractor") {
        setSendDocContractorLoading(true);
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/email/send-documents`,
        {
          sendTo: type,
          task_id: selectedRow?.task_id,
          cst_packageID: selectedRow?.cst_packageID,
          customer,
          currentSession,
        },
        {
          withCredentials: true,
        },
      );

      if (response?.data?.status === "success") {
        showDocumentMessage("success", response?.data?.message);
      } else {
        showDocumentMessage("error", response?.data?.message);
      }
    } catch (error) {
      showDocumentMessage(
        "error",
        error?.response?.data?.message || "An error occurred",
      );
    } finally {
      setSendDocOwnerLoading(false);
      setSendDocContractorLoading(false);
    }
  };

  const getDownloadFileName = () => {
    if (selectedRow?.cst_packageID) {
      if (selectedRow?.contractor?.ORGANIZATION?.nameUnparsed) {
        return selectedRow.contractor.ORGANIZATION.nameUnparsed;
      }

      if (selectedRow?.contractor?.PERSON?.business_name) {
        return selectedRow.contractor.PERSON.business_name;
      }

      if (selectedRow?.contractor?.PERSON?.first_name) {
        return `${selectedRow.contractor.PERSON.first_name} ${
          selectedRow?.contractor?.PERSON?.last_name || ""
        }`.trim();
      }

      return "customer_uploaded_noc";
    }

    return selectedRow?.contractor?.businessName || "permit_package";
  };

  const handleDownloadCustomerDocs = async () => {
    try {
      setDocumentDownloadLoading(true);

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/data/admin/download-customer-documents`,
        {
          pdf: "all",
          currentSession,
          type: "all",
          task_id: selectedRow?.task_id,
          cst_packageID: selectedRow?.cst_packageID,
          customer,
        },
        {
          withCredentials: true,
          responseType: "blob",
        },
      );

      const contentType = response.headers["content-type"];

      if (contentType && contentType.includes("application/json")) {
        const text = await new Response(response.data).text();
        const errorData = JSON.parse(text);

        if (errorData.success === false) {
          showDocumentMessage(
            "error",
            errorData.message || "Document not ready yet",
          );
          return;
        }
      }

      let fileExtension = "pdf";
      if (contentType === "image/png") {
        fileExtension = "png";
      } else if (contentType === "image/jpeg") {
        fileExtension = "jpg";
      } else if (contentType === "application/zip") {
        fileExtension = "zip";
      }

      const fileURL = window.URL.createObjectURL(
        new Blob([response.data], { type: contentType }),
      );
      const fileLink = document.createElement("a");
      fileLink.href = fileURL;
      fileLink.setAttribute(
        "download",
        `${getDownloadFileName()}.${fileExtension}`,
      );
      document.body.appendChild(fileLink);
      fileLink.click();
      document.body.removeChild(fileLink);
      window.URL.revokeObjectURL(fileURL);
    } catch (error) {
      showDocumentMessage(
        "error",
        error?.response?.data?.message || "Document not ready yet",
      );
    } finally {
      setDocumentDownloadLoading(false);
    }
  };

  const handleCopyUrl = async (url, urlKey) => {
    if (!url) return;

    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrlKey(urlKey);
      setTimeout(() => setCopiedUrlKey(null), 900);
    } catch (error) {
      showDocumentMessage("error", "Unable to copy URL to the clipboard.");
    }
  };

  const getAccordionDisabledState = () => {
    const notarization = selectedRow?.processing_permit?.notarization;
    const contractor = notarization?.contractor?.notarization;

    if (
      !selectedRow?.processing_permit ||
      Object.keys(selectedRow.processing_permit || {}).length === 0
    ) {
      return {
        disabled: true,
        message: "Notarization/eSign has not been started yet by the user.",
      };
    }

    const documents = selectedRow?.documents || {};
    if (documents.noc_affidavit) {
      return {
        disabled: true,
        message:
          "Cannot change sign order: NOC Affidavit requires owner to sign first.",
      };
    }

    if (notarization?.finalized === true) {
      return {
        disabled: true,
        message: "Notarization has been finalized and cannot be modified.",
      };
    }

    if (notarization?.started === false) {
      return {
        disabled: true,
        message: "Notarization has not been started yet.",
      };
    }

    if (contractor?.is_notarized === true) {
      return {
        disabled: true,
        message:
          "Contractor has already been notarized. Sign order cannot be changed.",
      };
    }

    if (contractor?.has_notary === true) {
      return {
        disabled: true,
        message:
          "Contractor has been assigned a notary. Sign order cannot be changed.",
      };
    }

    if (contractor?.bn_id && contractor.bn_id !== "") {
      return {
        disabled: true,
        message:
          "Contractor has a BlueNotary ID assigned. Sign order cannot be changed.",
      };
    }

    if (
      contractor?.appointments?.booked &&
      Array.isArray(contractor.appointments.booked) &&
      contractor.appointments.booked.length > 0
    ) {
      return {
        disabled: true,
        message:
          "Contractor has appointments booked. Sign order cannot be changed.",
      };
    }

    const owner = notarization?.owner?.notarization;
    if (owner?.is_notarized === true) {
      return {
        disabled: true,
        message:
          "Owner has already been notarized. Sign order cannot be changed.",
      };
    }

    if (owner?.has_notary === true) {
      return {
        disabled: true,
        message:
          "Owner has been assigned a notary. Sign order cannot be changed.",
      };
    }

    if (owner?.bn_id && owner.bn_id !== "") {
      return {
        disabled: true,
        message:
          "Owner has a BlueNotary ID assigned. Sign order cannot be changed.",
      };
    }

    if (
      owner?.appointments?.booked &&
      Array.isArray(owner.appointments.booked) &&
      owner.appointments.booked.length > 0
    ) {
      return {
        disabled: true,
        message: "Owner has appointments booked. Sign order cannot be changed.",
      };
    }

    return {
      disabled: false,
      message: null,
    };
  };

  const handleConfirmModify = async () => {
    try {
      setModifyLoading(true);
      setSignOrderMessage({ type: "", text: "" });

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/change-sign-order`,
        {
          task_id: selectedRow?.task_id,
          signing_order: selectedSignOrder,
          currentSession,
        },
        {
          withCredentials: true,
        },
      );

      if (response?.data?.success) {
        const responsePackage = response?.data?.data || {};
        const updatedPackage = {
          ...selectedRow,
          ...responsePackage,
          processing_permit: {
            ...selectedRow?.processing_permit,
            ...responsePackage?.processing_permit,
            notarization: {
              ...selectedRow?.processing_permit?.notarization,
              ...responsePackage?.processing_permit?.notarization,
              signing_order: selectedSignOrder,
            },
          },
        };

        const isSamePackage = (permitPackage) =>
          (updatedPackage?.task_id &&
            permitPackage?.task_id === updatedPackage.task_id) ||
          (updatedPackage?.cst_packageID &&
            permitPackage?.cst_packageID === updatedPackage.cst_packageID) ||
          (updatedPackage?.id && permitPackage?.id === updatedPackage.id);

        const isCustomerPackagesPage =
          typeof window !== "undefined" &&
          window.location.pathname.includes("/permits/customers-packages");

        if (isCustomerPackagesPage && Array.isArray(customerPackages)) {
          dispatch({
            type: "GET_CUSTOMER_PACKAGES",
            payload: customerPackages.map((permitPackage) =>
              isSamePackage(permitPackage) ? updatedPackage : permitPackage,
            ),
          });
        } else if (Array.isArray(permitPackages)) {
          dispatch({
            type: "GET_PERMIT_PACKAGES",
            payload: permitPackages.map((permitPackage) =>
              isSamePackage(permitPackage) ? updatedPackage : permitPackage,
            ),
          });
        }

        dispatch(setSelectedRow(updatedPackage));

        setOriginalSignOrder(selectedSignOrder);
        setAccordionExpanded(false);
        setConfirmModalOpen(false);
        setSignOrderMessage({
          type: "success",
          text: response?.data?.message || "Sign order changed successfully",
        });
        setTimeout(
          () => setSignOrderMessage({ type: "", text: "" }),
          MESSAGE_DURATION,
        );
      } else {
        setSignOrderMessage({
          type: "error",
          text: response?.data?.message || "Failed to modify sign order",
        });
        setConfirmModalOpen(false);
      }
    } catch (error) {
      setSignOrderMessage({
        type: "error",
        text:
          error?.response?.data?.message ||
          "An error occurred while modifying sign order",
      });
      setConfirmModalOpen(false);
    } finally {
      setModifyLoading(false);
    }
  };

  if (!isAdmin && !isSubAdmin) return null;

  const accordionState = getAccordionDisabledState();
  const hasSignOrderChanged = selectedSignOrder !== originalSignOrder;
  const documentActionLoading =
    documentDownloadLoading || sendDocOwnerLoading || sendDocContractorLoading;

  return (
    <>
      <Stack spacing={2.5} sx={{ p: 2 }}>
        {/* Documents */}
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <SectionHeader
            icon={<DescriptionOutlinedIcon color="primary" fontSize="small" />}
            title="Documents"
          />

          {documentMessage.text && (
            <Alert
              severity={
                documentMessage.type === "success" ? "success" : "error"
              }
              sx={{ mb: 2 }}
              onClose={() => setDocumentMessage({ type: "", text: "" })}
            >
              {documentMessage.text}
            </Alert>
          )}

          <Stack spacing={1.25}>
            <LoadingButton
              onClick={() => handleSendDocument("owner")}
              loading={sendDocOwnerLoading}
              disabled={documentDownloadLoading || sendDocContractorLoading}
              variant="contained"
              startIcon={<PersonOutlineIcon />}
              fullWidth
              sx={{ justifyContent: "flex-start", borderRadius: 1.5, py: 1 }}
            >
              Send Documents to Property Owner
            </LoadingButton>
            <LoadingButton
              onClick={() => handleSendDocument("contractor")}
              loading={sendDocContractorLoading}
              disabled={documentDownloadLoading || sendDocOwnerLoading}
              variant="contained"
              startIcon={<EngineeringOutlinedIcon />}
              fullWidth
              sx={{ justifyContent: "flex-start", borderRadius: 1.5, py: 1 }}
            >
              Send Documents to Contractor
            </LoadingButton>
            <LoadingButton
              loading={documentDownloadLoading}
              disabled={sendDocOwnerLoading || sendDocContractorLoading}
              onClick={handleDownloadCustomerDocs}
              variant="outlined"
              fullWidth
              sx={{ justifyContent: "flex-start", borderRadius: 1.5, py: 1 }}
            >
              Download Documents
            </LoadingButton>
          </Stack>
        </Paper>

        {/* Session URLs */}
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <SectionHeader
            icon={<LinkOutlinedIcon color="primary" fontSize="small" />}
            title="Session URLs"
          />

          {sessionLinks.length > 0 ? (
            <Stack spacing={2}>
              {sessionLinks.map(({ key, label, icon, session }) => (
                <Box key={key}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.75}
                    sx={{ mb: 1 }}
                  >
                    {icon}
                    <Typography variant="body2" fontWeight={700}>
                      {label}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    {[
                      {
                        urlKey: `${key}-signer`,
                        url: session?.signer_url,
                        label: "Signer URL",
                      },
                      {
                        urlKey: `${key}-notary`,
                        url: session?.notary_url,
                        label: "Notary URL",
                      },
                    ].map(({ urlKey, url, label: btnLabel }) => (
                      <Tooltip
                        key={urlKey}
                        title="Copied"
                        placement="top"
                        open={copiedUrlKey === urlKey}
                        disableFocusListener
                        disableHoverListener
                        disableTouchListener
                      >
                        <span style={{ flex: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            fullWidth
                            disabled={!url}
                            startIcon={
                              copiedUrlKey === urlKey ? (
                                <CheckIcon fontSize="small" />
                              ) : (
                                <ContentCopyIcon fontSize="small" />
                              )
                            }
                            onClick={() => handleCopyUrl(url, urlKey)}
                            sx={{ borderRadius: 1.5 }}
                          >
                            {btnLabel}
                          </Button>
                        </span>
                      </Tooltip>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Signer and notary URLs are not available for this package.
            </Typography>
          )}
        </Paper>

        {/* Notarization / eSign */}
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <SectionHeader
            icon={<EditNoteOutlinedIcon color="primary" fontSize="small" />}
            title="Notarization/eSign"
          />

          <Accordion
            expanded={accordionExpanded}
            onChange={(_, isExpanded) => setAccordionExpanded(isExpanded)}
            disabled={accordionState.disabled}
            disableGutters
            elevation={0}
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1.5,
              "&:before": { display: "none" },
              "&.Mui-expanded": { margin: 0 },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="admin-sign-order-content"
              id="admin-sign-order-header"
            >
              <Typography variant="body2" fontWeight={700}>
                Change Notarization/Sign Order
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <ToggleButtonGroup
                  value={selectedSignOrder}
                  exclusive
                  onChange={(_, value) => value && setSelectedSignOrder(value)}
                  color="primary"
                  fullWidth
                  size="small"
                >
                  <ToggleButton
                    value="owner"
                    sx={{ textTransform: "none", py: 1 }}
                  >
                    Owner
                  </ToggleButton>
                  <ToggleButton
                    value="contractor"
                    sx={{ textTransform: "none", py: 1 }}
                  >
                    Contractor
                  </ToggleButton>
                </ToggleButtonGroup>

                <Button
                  onClick={() => setConfirmModalOpen(true)}
                  variant="contained"
                  disabled={!hasSignOrderChanged}
                  sx={{ alignSelf: "flex-start", borderRadius: 1.5 }}
                >
                  Modify
                </Button>
              </Stack>
            </AccordionDetails>
          </Accordion>

          {signOrderMessage.text && (
            <Alert
              severity={
                signOrderMessage.type === "success" ? "success" : "error"
              }
              sx={{ mt: 2 }}
              onClose={() => setSignOrderMessage({ type: "", text: "" })}
            >
              {signOrderMessage.text}
            </Alert>
          )}

          {accordionState.disabled && accordionState.message && (
            <Chip
              label={accordionState.message}
              color="warning"
              variant="outlined"
              size="small"
              sx={{
                mt: 2,
                height: "auto",
                "& .MuiChip-label": { whiteSpace: "normal", py: 0.75 },
              }}
            />
          )}
        </Paper>
      </Stack>

      <Dialog
        open={confirmModalOpen}
        onClose={modifyLoading ? undefined : () => setConfirmModalOpen(false)}
        aria-labelledby="admin-confirm-sign-order-title"
        aria-describedby="admin-confirm-sign-order-description"
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle id="admin-confirm-sign-order-title">
          Confirm Sign Order Change
        </DialogTitle>
        <DialogContent>
          <Typography id="admin-confirm-sign-order-description">
            Are you sure you want to change the notarization/sign order to{" "}
            <strong>
              {selectedSignOrder === "owner" ? "Owner" : "Contractor"}
            </strong>
            ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmModalOpen(false)}
            color="inherit"
            disabled={modifyLoading}
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleConfirmModify}
            variant="contained"
            loading={modifyLoading}
            disabled={documentActionLoading}
            autoFocus
          >
            Confirm
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default React.memo(AdminPackageTab);
